<!--
  Related-records list — the read page's alternative to BreadDataTable.

  A relation tab answers "which roles does this user have?", not "compare these
  rows column by column". A table spends its width on repeating the same two or
  three headers down a page that rarely holds more than a handful of rows; this
  spends it on the record's own identity instead: one row per related record,
  its name leading, everything else demoted to meta beneath it.

  It is NOT a second data layer. The request is byte-identical to the one
  BreadDataTable sends (same buildUrl/buildBrowseParams port of Tabulator's
  serializer), so the backend sees no difference, and the public methods a page
  drives a table with — getFilterCondition(), refresh() — are the same names
  with the same meanings.

  Which column becomes the row's title is derived, not configured per model:
  `childPrefix` names the model the tab is really about (the far side of an
  intermediate table), so on a user's Roles tab "InternalRole.name" leads and
  "InternalUser.name" is dropped as `parentPrefix` — you are already on that
  user's page.

  Values are rendered as TEXT, matching BreadDataRecordView. The one exception
  is the Actions column, which is a model-authored formatter returning a mounted
  component (Detach/Remove) and so goes through BreadDataTableCell exactly as it
  would in the table.
-->
<template>
  <div class="brl">
    <div v-if="loading" class="brl__state">Loading…</div>
    <div v-else-if="error" class="brl__state brl__state--error">
      Failed to load records.
    </div>
    <div v-else-if="!rows.length" class="brl__state">{{ emptyText }}</div>

    <ul v-else class="brl__list">
      <li v-for="(row, i) in rows" :key="rowKey(row, i)" class="brl__row">
        <span class="brl__avatar" aria-hidden="true">{{
          initials(primaryText(row))
        }}</span>

        <div class="brl__identity">
          <div class="brl__primary">{{ primaryText(row) || "—" }}</div>
          <div v-if="metaColumns.length" class="brl__meta">
            <span
              v-for="h in metaColumns"
              :key="h.value"
              class="brl__meta-item"
            >
              <span class="brl__meta-label">{{ h.text }}</span>
              <span class="brl__meta-value">{{ cellText(row, h) }}</span>
            </span>
          </div>
        </div>

        <div v-if="actionColumn" class="brl__actions">
          <BreadDataTableCell
            :row="row"
            :header="actionColumn"
            :table-data="rows"
          />
        </div>
      </li>
    </ul>

    <div v-if="!loading && totalPages > 1" class="brl__pager">
      <span class="brl__count">{{ counterText }}</span>
      <div class="brl__pager-btns">
        <button class="brl__pg" :disabled="page <= 1" @click="goTo(page - 1)">
          Prev
        </button>
        <button
          v-for="p in pageWindow"
          :key="p"
          class="brl__pg brl__pg--num"
          :class="{ 'brl__pg--active': p === page }"
          @click="goTo(p)"
        >
          {{ p }}
        </button>
        <button
          class="brl__pg"
          :disabled="page >= totalPages"
          @click="goTo(page + 1)"
        >
          Next
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import store from "@/store";
import BreadDataTableCell from "./breadDataTable/BreadDataTableCell.vue";
import { buildUrl, buildBrowseParams } from "./breadDataTable/serializeQuery";
import { adaptColumns } from "./breadDataTable/columnAdapter";
import { getFieldValue } from "./breadDataTable/fieldValue";

/** Anything the API hands back that is really a timestamp, however it's typed. */
const ISO_DATE = /^\d{4}-\d{2}-\d{2}([T ]\d{2}:\d{2}|$)/;

export default {
  name: "BreadDataRecordList",
  components: { BreadDataTableCell },
  props: {
    apiUrl: { type: String, default: "" },
    /** Raw Tabulator column definitions — `model.browse.table.headers`. */
    columns: { type: Array, default: () => [] },
    /**
     * PascalCase model name whose column should title each row, e.g.
     * "InternalRole" on a user's Roles tab. Matched as a `Field.` prefix.
     */
    childPrefix: { type: String, default: "" },
    /** PascalCase name of the record we're already on; its columns are dropped. */
    parentPrefix: { type: String, default: "" },
    /** Exact field names to drop — the relation key the tab already filters by. */
    hiddenFields: { type: Array, default: () => [] },
    emptyText: { type: String, default: "No records yet." },
    paginationSize: { type: Number, default: 10 },
  },
  data() {
    return {
      rows: [],
      total: 0,
      totalPages: 1,
      page: 1,
      loading: false,
      error: null,
      filters: [],
      currentUrl: this.apiUrl,
    };
  },
  computed: {
    /**
     * Reuses the table's adapter rather than reading the model defs directly,
     * so a column hidden from the table (`visible: false`, the row-selection
     * checkbox) is hidden here too, and `_def` is available for the action cell.
     */
    adapted() {
      return adaptColumns(this.columns).headers;
    },
    /**
     * Falls back to the unfiltered set when the drop rules would empty it —
     * a relation whose only column belongs to the parent is better shown
     * redundantly than shown blank.
     */
    fieldColumns() {
      const all = this.adapted.filter((h) => h.field);
      const parent = this.parentPrefix ? `${this.parentPrefix}.` : null;
      const kept = all.filter(
        (h) =>
          this.hiddenFields.indexOf(h.field) === -1 &&
          !(parent && h.field.indexOf(parent) === 0)
      );
      return kept.length ? kept : all;
    },
    primaryColumn() {
      const child = this.childPrefix ? `${this.childPrefix}.` : null;
      const named =
        child && this.fieldColumns.find((h) => h.field.indexOf(child) === 0);
      return named || this.fieldColumns[0] || null;
    },
    metaColumns() {
      return this.fieldColumns.filter((h) => h !== this.primaryColumn);
    },
    /**
     * Fieldless columns are the Index and the Actions cell; only the second one
     * survives into a list, and its title is what tells them apart. Both
     * spellings are in use across the model files.
     */
    actionColumn() {
      return this.adapted.find((h) => !h.field && /^actions?$/i.test(h.text)) || null;
    },
    counterText() {
      return `${this.total} ${this.total === 1 ? "record" : "records"}`;
    },
    pageWindow() {
      const total = this.totalPages || 1;
      const start = Math.max(1, Math.min(this.page - 1, total - 2));
      const out = [];
      for (let p = start; p < start + 3 && p <= total; p++) out.push(p);
      return out;
    },
    requestKey() {
      return JSON.stringify({
        url: this.currentUrl,
        page: this.page,
        size: this.paginationSize,
        filter: this.filters,
      });
    },
  },
  watch: {
    requestKey: "scheduleFetch",
    apiUrl(v) {
      this.currentUrl = v;
    },
  },
  created() {
    this._seq = 0;
    this._pendingTick = false;
  },
  mounted() {
    this.scheduleFetch();
  },
  beforeDestroy() {
    if (this._source) this._source.cancel("component destroyed");
  },
  methods: {
    /** Collapses a burst of state changes into one request, as the table does. */
    scheduleFetch() {
      if (this._pendingTick) return;
      this._pendingTick = true;
      this.$nextTick(() => {
        this._pendingTick = false;
        this.fetch();
      });
    },
    fetch() {
      if (!this.currentUrl) return;

      if (this._source) this._source.cancel("superseded");
      this._source = axios.CancelToken.source();

      const seq = ++this._seq;
      const page = this.page;
      this.loading = true;
      this.error = null;

      const params = buildBrowseParams({
        page,
        size: this.paginationSize,
        filter: this.filters,
      });

      axios
        .get(buildUrl(this.currentUrl, params), {
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + store.state.auth.data.token,
          },
          cancelToken: this._source.token,
        })
        .then((res) => {
          if (seq !== this._seq) return; // a newer request already landed
          const body = res.data || {};
          const info = body.info || {};
          this.rows = body.data || [];
          this.total = info.totalItems || 0;
          this.totalPages = info.totalPages || 1;
          this.loading = false;
          // A page restored from elsewhere can land past the end once the real
          // count is known; snapping back can't recur, the snapped page is in
          // range by definition.
          if (page > this.totalPages) this.page = this.totalPages;
          this.$emit("total", this.total);
        })
        .catch((err) => {
          if (axios.isCancel(err)) return;
          if (seq !== this._seq) return;
          this.loading = false;
          this.error = err;
          this.rows = [];
        });
    },
    goTo(p) {
      const next = Math.max(1, Math.min(parseInt(p, 10) || 1, this.totalPages || 1));
      if (next !== this.page) this.page = next;
    },
    rowKey(row, i) {
      return row && row.id != null ? `id-${row.id}` : `i-${i}`;
    },
    primaryText(row) {
      return this.primaryColumn ? this.cellText(row, this.primaryColumn) : "";
    },
    cellText(row, header) {
      const raw = getFieldValue(row, header.field);
      if (raw === null || raw === undefined || raw === "") return "—";
      if (typeof raw === "boolean") return raw ? "Yes" : "No";

      const looksLikeDate =
        header.dataType === "date" ||
        /^timestamp/i.test(header.field || "") ||
        (typeof raw === "string" && ISO_DATE.test(raw));

      if (looksLikeDate) {
        const m = this.$moment(new Date(raw));
        if (m.isValid()) return m.format("DD MMM YYYY, HH:mm");
      }

      if (typeof raw === "object") return JSON.stringify(raw);
      return String(raw);
    },
    initials(text) {
      const words = String(text || "")
        .trim()
        .split(/[\s._-]+/)
        .filter(Boolean);
      if (!words.length) return "–";
      return (words[0][0] + (words[1] ? words[1][0] : ""))
        .toUpperCase()
        .slice(0, 2);
    },

    // ── public API, named as on BreadDataTable so a tab swaps by tag rename ──
    getFilterCondition(conditions) {
      this.filters = Array.isArray(conditions) ? conditions : [];
      this.page = 1;
      this.scheduleFetch();
    },
    refresh() {
      this.scheduleFetch();
    },
  },
};
</script>

<style scoped>
/* Colour is token-only (--app-* / --tbl-*), so light and dark are both covered
   without a companion `.dark-mode` block to keep in sync. */
.brl {
  font-family: var(--tbl-font);
}

.brl__list {
  list-style: none;
  margin: 0;
  padding: 0;
  border: 1px solid var(--app-border);
  border-radius: var(--tbl-radius);
  overflow: hidden;
  background: var(--app-card);
}

.brl__row {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 16px;
  border-bottom: 1px solid var(--app-border);
  transition: background 0.15s ease;
}

.brl__row:last-child {
  border-bottom: none;
}

.brl__row:hover {
  background: var(--app-hover);
}

.brl__avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: var(--app-accent-soft);
  border: 1px solid var(--app-accent-border);
  color: var(--app-accent-strong);
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.3px;
}

.brl__identity {
  flex: 1 1 auto;
  min-width: 0;
}

.brl__primary {
  font-size: 0.88rem;
  font-weight: 700;
  color: var(--app-text);
  overflow-wrap: anywhere;
}

.brl__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 16px;
  margin-top: 3px;
}

.brl__meta-item {
  display: inline-flex;
  align-items: baseline;
  gap: 5px;
  min-width: 0;
}

.brl__meta-label {
  font-size: 0.66rem;
  font-weight: 800;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: var(--app-text-faint);
}

.brl__meta-value {
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--app-text-muted);
  overflow-wrap: anywhere;
}

.brl__actions {
  flex: 0 0 auto;
}

/* The action cell is a table cell by origin and carries the table's row height
   and gutter; in a list its row already supplies both. */
.brl__actions >>> .bdt-cell {
  min-height: 0;
  padding: 0;
}

.brl__state {
  padding: 34px 16px;
  text-align: center;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--app-text-faint);
  border: 1px solid var(--app-border);
  border-radius: var(--tbl-radius);
  background: var(--app-card);
}

.brl__state--error {
  color: var(--app-danger-fg);
  background: var(--app-danger-bg);
  border-color: var(--app-danger-border);
}

.brl__pager {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 12px;
}

.brl__count {
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--app-text-muted);
}

.brl__pager-btns {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.brl__pg {
  min-width: 40px;
  height: 32px;
  padding: 0 12px;
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--app-text-muted);
  background: var(--app-card);
  border: 1px solid var(--app-border);
  border-radius: var(--tbl-radius);
  transition: all 0.15s ease;
}

.brl__pg:hover:not([disabled]) {
  background: var(--app-elevated);
  border-color: var(--app-border-strong);
  color: var(--app-text);
}

.brl__pg[disabled] {
  color: var(--app-text-faint);
  cursor: default;
}

.brl__pg--num {
  min-width: 34px;
  padding: 0 8px;
}

.brl__pg--active,
.brl__pg--active:hover {
  background: var(--app-accent) !important;
  border-color: var(--app-accent) !important;
  color: var(--app-accent-fg) !important;
}

@media (max-width: 599px) {
  .brl__row {
    align-items: flex-start;
    gap: 10px;
    padding: 11px 12px;
  }

  .brl__meta {
    gap: 2px 12px;
  }
}
</style>
